



























































































































































































































































































































































































import { Component, Prop, Watch, Mixins, Inject } from "vue-property-decorator";
import { mapGetters, mapState } from "vuex";
import { ValidationObserver } from "vee-validate";
import _ from "@/boot/lodash";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import HasValidation from "@/mixins/HasValidation";

// Enums/Data
import { ProductTypes } from "@/data/enums/products";
import { DataModules } from "@/store/modules/data";
import { ImageObjectTypes } from "@/models/images";
import { ImageUploadTypes, QUERY_PARAMS } from "@/data/constants";
import { TranslationsTypes } from "@/data/enums/translations";

// Types
import { IProduct, IProductForm, ISelectedProduct } from "@/models/products";
import { IProductProviderData } from "@/models/providers/products";
import { IState } from "@/store";
import { SortableItems } from "@/data/enums/sortableItems";
import { UUID, ProductOrderTypes } from "@/data/enums";
import { IUploadFile } from "@/models/uploadFiles";
import { IBrand } from "@/models/brands";
import { AxiosError } from "axios";

@Component({
  components: {
    "product-gallery": () =>
      import("@/components/app/admin/catalogue/products/productGallery.vue"),
    "category-picker": () =>
      import("@/components/app/admin/catalogue/products/categoryPicker.vue"),
    "multilevel-categories-selections": () =>
      import(
        "@/components/app/admin/catalogue/products/multilevelCategories/multilevelCategoriesSelections.vue"
      ),
    "product-is-active-checkbox": () => import("./productIsActiveCheckbox.vue"),
    "product-is-visible-checkbox": () =>
      import("./productIsVisibleCheckbox.vue"),
    "product-hide-catalogue-checkbox": () =>
      import("./productHideCatalogueCheckbox.vue")
  },
  computed: {
    ...mapGetters({
      oauthClients: "auth/client/oauthClients"
    }),
    ...mapState({
      productScope(state: IState, getters) {
        return getters[`data/${DataModules.CATALOGUE}/scope`](this.productId);
      },
      product(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: DataModules.CATALOGUE,
          scope: this.productScope
        });
      }
    })
  }
})
export default class EditProduct extends Mixins(
  RequestPromises,
  HasValidation
) {
  public declare $refs: {
    observer: InstanceType<typeof ValidationObserver>;
    name: HTMLFormElement;
  };

  // :getters
  public product!: IProduct;
  public productScope!: string;

  public oauthClients!: any;

  // Inject
  @Inject({ from: "productProviderData" })
  public readonly $productProviderData!: () => IProductProviderData;

  @Prop({ required: true })
  public productId!: IProduct["id"];

  @Prop({ required: true })
  public productTypes!: ProductTypes[];

  public form = {} as IProductForm;
  public formClone = {} as IProductForm;

  public selectedProducts: ISelectedProduct[] = [];
  public selectedCategories: any[] = [];

  public isLoading = false;
  public isReloading = false;
  public isProcessing = false;
  public requestError = false;

  public brandId: IBrand["id"] = UUID.ORG;
  public savedCategoryId = 0;

  public imageModule: string = DataModules.IMAGES;
  public imageType = ImageObjectTypes.PRODUCT;
  public image = {} as IUploadFile;
  public imageUploadTypes = ImageUploadTypes;

  public limitQuantity = false;
  public savedMinQuantity = 0;
  public savedMaxQuantity = 0;

  public created() {
    if (this.contentAvailable) {
      this.isLoading = true;
      this.loadData();
    }
  }

  public async loadData() {
    try {
      await this.getProductData();
      this.refreshForm();
    } catch (e) {
      const error = e as Error;
      this.$buefy.toast.open({
        message: error.message,
        type: "is-danger"
      });
      this.requestError = true;
    }
    this.isLoading = false;
    this.isReloading = false;
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public getProductData(ignoreStored = false) {
    return this.$store.dispatch(`data/${DataModules.CATALOGUE}/get`, {
      id: this.productId,
      scope: this.productScope,
      vm: this,
      ignoreStored: ignoreStored
    });
  }

  public refreshForm() {
    this.brandId = _.get(this.product, "brand_id", UUID.ORG);

    this.selectedProducts = _.map(
      _.get(this.product, "set_products", []),
      (product, index: number) => {
        return _.merge(_.pick(product, ["id", "name", "category_id"]), {
          order: index,
          type: SortableItems.ITEM
        });
      }
    );

    this.form = _.merge(
      {},
      _.pick(this.product, [
        "name",
        "description",
        "short_description",
        "product_type",
        "available_for_sales",
        "clients_can_order",
        "hide_catalog",
        this.productTypes.includes(ProductTypes.PRODUCT_OPTION)
          ? "products_options_category_id"
          : this.productTypes.includes(ProductTypes.PRODUCT_ATTRIBUTE)
          ? "products_attributes_category_id"
          : "products_category_id",
        "order_type",
        "unit_quantity",
        "min_order_quantity",
        "max_order_quantity"
      ]),
      {
        set_products: _.map(
          _.get(this.product, "set_products", []),
          product => {
            return { product_id: product.id };
          }
        )
      }
    ) as IProductForm;

    this.$set(this.form, "order_type", this.orderType);
    this.$set(
      this.form,
      "unit_quantity",
      _.get(this.form, "unit_quantity", "").toString()
    );

    this.formClone = _.cloneDeep(this.form);

    this.form.set_products = _.map(this.selectedProducts, product => {
      return { product_id: product.id };
    });

    this.savedCategoryId = this.form[this.getCategoryFieldName];
    this.savedMaxQuantity = this.product.max_order_quantity;
    this.savedMinQuantity = this.product.min_order_quantity;
    this.limitQuantity =
      !!this.product.max_order_quantity || !!this.product.min_order_quantity;
  }

  public onSelectedProducts(products) {
    this.selectedProducts = _.map(products, product => {
      product.type = SortableItems.ITEM;
      return product;
    });
  }

  public get orderTypes() {
    return _.concat(
      this.isBundle || this.isSingleProduct
        ? []
        : [
            {
              value: ProductOrderTypes.INHERIT,
              key: "3_inherit",
              label: this.$t("_.inherit_quantity_from_main_product")
            }
          ],
      [
        {
          value: ProductOrderTypes.QUANTITY_BASED,
          key: "2_yes",
          label: this.$t("_.yes")
        },
        {
          value: ProductOrderTypes.QUANTITY_BASED,
          key: "2_yes_multiple",
          label: this.$t("_.yes_multiple")
        },
        {
          value: ProductOrderTypes.SINGLE_OPTION,
          key: "1_no",
          label: this.$t("_.no")
        }
      ]
    );
  }

  public get productTypesEnums() {
    return ProductTypes;
  }

  public get isBundle() {
    return this.form.product_type === ProductTypes.PRODUCT_BUNDLE;
  }

  public get isSingleProduct() {
    return this.form.product_type === ProductTypes.SINGLE_PRODUCT;
  }

  public get canManageProduct() {
    return this.$userCan("update_product");
  }

  public get isFormChanged(): boolean {
    return !_.isEqual(this.form, this.formClone);
  }

  public get productData() {
    return this.$productProviderData();
  }

  public get isStaged() {
    return this.productData.isStaged;
  }

  public get productStoreLink() {
    const origins = _.filter(this.oauthClients, ["customer_enabled", true]);
    const origin = (_.find(origins, ["default", true]) || origins[0])?.origin;
    const resolvedRoute = this.$router.resolve({
      name: "orderProductViewStep",
      query: {
        [QUERY_PARAMS.PRODUCT_ID]: this.product.id
      }
    });
    return [origin, resolvedRoute.href].join("");
  }

  public revertToInitialState() {
    this.form = _.cloneDeep(this.formClone);

    this.selectedProducts = _.map(
      _.get(this.product, "set_products", []),
      (product, index: number) => {
        return _.merge(_.pick(product, ["id", "name", "category_id"]), {
          order: index,
          type: SortableItems.ITEM
        });
      }
    );
  }

  public get canTranslateProduct() {
    return (
      this.$userCan("create_object_translation") &&
      this.$userCan("get_object_translation") &&
      this.$userCan("list_object_translations") &&
      this.canManageProduct &&
      !this.isStaged
    );
  }

  public get getCategoryFieldName(): string {
    return this.productTypes.includes(ProductTypes.PRODUCT_OPTION)
      ? "products_options_category_id"
      : this.productTypes.includes(ProductTypes.PRODUCT_ATTRIBUTE)
      ? "products_attributes_category_id"
      : "products_category_id";
  }

  public get formIsValid() {
    if (_.isEqual(this.form, this.formClone)) {
      return false;
    }

    if (this.isBundle && !this.form.set_products.length) {
      return false;
    }

    return true;
  }

  public get formIsDisabled() {
    return (
      this.isProcessing || !this.formIsValid || this.hasErrors || this.isLoading
    );
  }

  public async validateBeforeSubmit() {
    if (this.canManageProduct) {
      this.isProcessing = true;

      try {
        const result = await this.validate();
        if (!result) {
          throw this.$t("_sentence.form_is_not_valid");
        }

        await this.manageProductImage();
        await this.updateProduct();

        await this.getProductData(true);
        this.refreshForm();
        this.$buefy.toast.open({
          message: this.$t("_.object_successfully_updated", {
            object: this.productTypes.includes(ProductTypes.PRODUCT_OPTION)
              ? this.$t("_.product_option")
              : this.productTypes.includes(ProductTypes.PRODUCT_ATTRIBUTE)
              ? this.$t("_.product_attribute")
              : this.$t("_.product")
          }) as string
        });
      } catch (error) {
        this.$store.dispatch("api/handleValidationError", {
          error,
          vm: this
        });
      }

      this.isProcessing = false;
    }
  }

  public updateProduct() {
    return this.$store.dispatch(`data/${DataModules.CATALOGUE}/update`, {
      id: this.productId,
      scope: this.productScope,
      data: {
        ...this.getNewValues()
      }
    });
  }

  public manageProductImage() {
    if (this.form.productImage === this.formClone.productImage) {
      return Promise.resolve();
    }

    if (this.form.productImage === null) {
      return this.deleteProductImage();
    }

    return this.uploadImage();
  }

  public async uploadImage() {
    const data = new FormData();
    data.append("image", _.get(this.image, "file.fileObj"));

    try {
      const result = await this.$store.dispatch(
        `data/${this.imageModule}/uploadImage`,
        {
          typeId: this.productId,
          imageType: this.imageType,
          isDefault: true,
          data,
          onUploadProgress: progressEvent => {
            this.image = _.merge(this.image, {
              progress: Math.floor(
                (progressEvent.loaded * 100) / progressEvent.total
              ),
              isUploading: true
            });
          },
          vm: this
        }
      );

      this.image = _.merge(this.image, {
        progress: 0,
        isUploading: false,
        hash: result.value
      });
      return Promise.resolve();
    } catch (e) {
      const error = e as AxiosError;
      if (error?.response?.data?.error?.data?.image) {
        error.response.data.error.data.productImage =
          error.response.data.error.data.image;
        delete error?.response?.data.error.data.image;
      }
      throw error;
    }
  }

  public deleteProductImage() {
    return this.$store.dispatch(`data/${this.imageModule}/deleteImage`, {
      typeId: this.productId,
      imageType: this.imageType,
      hash: this.formClone.productImage,
      vm: this
    });
  }

  public detachItem(itemId: IProduct["id"]) {
    this.selectedProducts = _.filter(
      this.selectedProducts,
      selectedProduct => selectedProduct.id !== itemId
    );
  }

  public getNewValues() {
    const newValues: any = _.omitBy(this.form, (v, k) => {
      return this.formClone[k] === v;
    });

    if (this.isBundle && this.areProductSetsEqual) {
      delete newValues.set_products;
    }

    newValues.order_type = this.form.order_type[0];
    newValues.unit_quantity = this.form.unit_quantity;

    return newValues;
  }

  public get areProductSetsEqual() {
    return (
      _.map(this.form.set_products, formProduct => {
        return formProduct.product_id;
      }).join("_") ===
      _.map(this.formClone.set_products, formCloneProduct => {
        return formCloneProduct.product_id;
      }).join("_")
    );
  }

  public getTypeName(ending: string) {
    return _.find(
      this.productTypes,
      type => type === ProductTypes.PRODUCT_OPTION
    )
      ? `_option${ending}`
      : _.find(
          this.productTypes,
          type => type === ProductTypes.PRODUCT_ATTRIBUTE
        )
      ? `_attribute${ending}`
      : ending;
  }

  public openTranslateModal(props: Record<string, string>) {
    if (this.canTranslateProduct) {
      this.$store.dispatch(
        `data/${DataModules.TRANSLATIONS}/openTranslationsModal`,
        {
          props: {
            objectId: this.productId,
            objectTypeCode: TranslationsTypes.PRODUCT,
            brandId: this.brandId,
            fieldType: "text",
            ...props
          }
        }
      );
    }
  }

  public get orderType() {
    if (_.get(this.product, "order_type") === 3) {
      return "3_inherit";
    }

    if (_.get(this.product, "order_type") === 2) {
      if (_.get(this.product, "unit_quantity", 1) > 1) {
        return "2_yes_multiple";
      }
      return "2_yes";
    }

    return "1_no";
  }

  public get unitQuantityRules() {
    const rules = ["required"];

    rules.push(
      `min_value:${
        _.get(this.form, "min_order_quantity", 0) > 1
          ? this.form.min_order_quantity
          : "1"
      }`
    );

    if (this.form.max_order_quantity) {
      rules.push(`max_value:${this.form.max_order_quantity}`);
    }

    return rules.join("|");
  }

  @Watch("selectedProducts")
  public onSelectedProductsChanged() {
    if (_.get(this.product, "product_type") === ProductTypes.PRODUCT_BUNDLE) {
      this.form.set_products = _.map(this.selectedProducts, product => {
        return { product_id: product.id };
      });
    }
  }

  @Watch("limitQuantity")
  public onLimitQuantityChanged() {
    this.form.max_order_quantity = !this.limitQuantity
      ? 0
      : this.savedMaxQuantity;

    this.form.min_order_quantity = !this.limitQuantity
      ? 0
      : this.savedMinQuantity;
  }

  @Watch("form.max_order_quantity")
  public onMaxQuantityChanged(value, oldValue) {
    this.savedMaxQuantity = oldValue;
  }

  @Watch("form.min_order_quantity")
  public onMinQuantityChanged(value, oldValue) {
    this.savedMinQuantity = oldValue;
  }

  public onOrderTypeChange(orderType: string) {
    if (orderType === "1_no") {
      this.$set(this.form, "unit_quantity", 0);
    } else {
      this.$set(
        this.form,
        "unit_quantity",
        orderType === "2_yes_multiple" &&
          _.get(this.product, "unit_quantity") > 1
          ? _.get(this.product, "unit_quantity")
          : 1
      );
    }
  }

  public imageDeleted() {
    this.image = {} as IUploadFile;
  }

  public imageAdded(image) {
    this.image = {
      file: image,
      isUploading: false,
      progress: 0,
      hash: null
    } as IUploadFile;
  }

  public get contentAvailable() {
    const permissions = ["staff_get_product"];
    if (this.productTypes.includes(ProductTypes.SINGLE_PRODUCT)) {
      permissions.push("list_product_categories");
    } else if (this.productTypes.includes(ProductTypes.PRODUCT_OPTION)) {
      permissions.push("list_product_options_categories");
    } else if (this.productTypes.includes(ProductTypes.PRODUCT_ATTRIBUTE)) {
      permissions.push("list_product_attributes_categories");
    }
    return this.$userCan(...permissions);
  }

  public get guardFunctionalities() {
    return this.productTypes.includes(ProductTypes.PRODUCT_OPTION)
      ? ["staff_get_product", "list_product_options_categories"]
      : this.productTypes.includes(ProductTypes.PRODUCT_ATTRIBUTE)
      ? ["staff_get_product", "list_product_attributes_categories"]
      : ["staff_get_product", "list_product_categories"];
  }

  public beforeDestroy() {
    this.$store.dispatch("data/bin", {
      storeModule: DataModules.CATALOGUE,
      scope: this.productScope,
      vm: this
    });
  }
}
